import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldAlert, Gavel, LogOut, Send, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";

export default function BannedPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [appeal, setAppeal] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        if (!r.data.banned) { navigate("/"); return null; }
        setUser(r.data);
        return api.get("/support/tickets");
      })
      .then((r) => {
        if (r) setAppeal(r.data.find((t) => t.category === "ban-appeal" && t.status !== "closed") || null);
      })
      .catch(() => { localStorage.removeItem("nc_token"); localStorage.removeItem("nc_user"); navigate("/auth"); });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("nc_token");
    localStorage.removeItem("nc_user");
    navigate("/");
    window.location.reload();
  };

  const submitAppeal = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post("/support/tickets", { subject, category: "ban-appeal", message });
      setAppeal(r.data);
      toast.success("Appeal submitted — the studio will review it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="banned-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-texture opacity-40" />
      <motion.div className="relative w-full max-w-2xl flex flex-col gap-6" initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
        <div className="border border-red-500/40 bg-red-500/5 p-8 md:p-10">
          <div className="flex items-center gap-4 mb-6">
            <span className="w-14 h-14 border border-red-500/50 bg-red-500/10 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-7 h-7 text-red-400" />
            </span>
            <div>
              <p className="font-mono text-[9px] tracking-[0.4em] text-red-400 uppercase">Account Suspended</p>
              <h1 data-testid="banned-title" className="font-sans font-bold uppercase tracking-tight text-2xl md:text-3xl text-zinc-50 mt-1">
                Access Restricted
              </h1>
            </div>
          </div>

          <div className="border border-white/10 bg-black/40 p-5">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Official reason for this ban</p>
            <p data-testid="banned-reason" className="font-mono text-sm text-zinc-200 leading-relaxed">
              {user.ban_reason || "Violation of the Specpixel Entertainment terms of use."}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 font-mono text-[10px] text-zinc-600">
              <span>Account: {user.email}</span>
              {user.banned_at && <span>Issued: {String(user.banned_at).slice(0, 10)}</span>}
              <span>Issued by: Studio Administration</span>
            </div>
          </div>

          <p className="font-mono text-xs text-zinc-500 leading-relaxed mt-5">
            While suspended you cannot browse the website, the Dev Hub, or any community features.
            If you believe this action was taken in error, you may submit exactly one ban appeal below.
            Appeals are reviewed manually by studio administration.
          </p>
        </div>

        {appeal ? (
          <div data-testid="appeal-status" className="border border-nc-orange/40 bg-nc-orange/5 p-6">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-5 h-5 text-nc-orange" />
              <h2 className="font-sans font-bold uppercase text-base text-zinc-50">Appeal under review</h2>
            </div>
            <p className="font-mono text-xs text-zinc-400 leading-relaxed">
              Your appeal "{appeal.subject}" was received on {String(appeal.createdAt).slice(0, 10)} and is waiting for review.
              You will see the outcome here. Submitting further appeals is not possible.
            </p>
            {appeal.messages.length > 1 && (
              <div className="mt-4 border-t border-white/10 pt-4 flex flex-col gap-3">
                {appeal.messages.slice(1).map((m, i) => (
                  <div key={i} className="font-mono text-xs">
                    <span className={m.role === "admin" ? "text-nc-orange" : "text-zinc-400"}>{m.from}: </span>
                    <span className="text-zinc-300">{m.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <motion.form data-testid="appeal-form" onSubmit={submitAppeal} className="border border-white/10 bg-black/40 p-6 md:p-8 flex flex-col gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }}>
            <h2 className="font-sans font-bold uppercase text-base text-zinc-50 flex items-center gap-2">
              <Gavel className="w-4 h-4 text-nc-orange" /> Submit a Ban Appeal
            </h2>
            <div>
              <label className={labelCls}>Appeal subject *</label>
              <input data-testid="appeal-subject-input" required minLength={5} className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Appeal — I understand the reason and request a second chance" />
            </div>
            <div>
              <label className={labelCls}>Your statement *</label>
              <textarea data-testid="appeal-message-input" required minLength={10} rows={5} className={`${inputCls} resize-none`} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Explain why the ban should be lifted. Be honest and specific — vague appeals are declined." />
            </div>
            <button data-testid="appeal-submit-button" type="submit" disabled={sending} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors">
              <Send className="w-4 h-4" /> {sending ? "Submitting..." : "Submit Appeal"}
            </button>
          </motion.form>
        )}

        <button data-testid="banned-logout-button" onClick={logout} className="self-center font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-red-400 border border-white/10 px-5 py-2.5 flex items-center gap-2 transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </motion.div>
    </div>
  );
}
